import { DEFAULT_DESIGN_SYSTEM } from "@/core/constants";
import { cn } from "@/lib/cn";
import ThemesSelectPopover from "@/pages/editor/modules/design-system/modules/theme/components/themes-select-popover";
import {
  storydocThemes,
  type StorydocTheme,
} from "@/pages/editor/modules/design-system/modules/theme/data/storydoc-themes";
import { useGetThemesQuery } from "@/shared/services";
import type { TDesignSystem, TDesignSystemRoot } from "@/shared/types";
import {
  UpgradeLabel,
  useLockedAction,
} from "@components/plan-subscription-lock";
import Popover from "@components/ui/popover";
import { useAccountStoreShallow } from "@features/account-management";
import { track } from "@features/analytics";
import { useOpenDesignSystemDialog } from "@pages/account/modules/document-settings/hooks/use-open-design-system-dialog";
import {
  IconChevronRight,
  IconFileSpark,
  IconLayoutBoard,
  IconPalette,
  IconPlus,
  IconSelector,
} from "@tabler/icons-react";
import { useAppSelector } from "@store";
import { getDesignSystem } from "@store/story/selectors";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  useFileToStorydocStore,
  useFileToStorydocStoreShallow,
} from "../stores";
import {
  type CreatedThemeSnapshot,
  resolveCreatedTheme,
  snapshotThemeIds,
} from "../utils/find-created-theme-id";
import { trackImportPreJob } from "../utils/track-import";

const COLOR_KEYS: (keyof TDesignSystemRoot)[] = [
  "primaryColor",
  "secondaryColor",
  "themeColor3",
  "themeColor4",
];

const ROW_CLASS =
  "tw-flex tw-h-8 tw-w-full tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded tw-px-3 tw-text-sm tw-text-blue tw-outline-none tw-transition-colors hover:tw-bg-[#F3F2F5] [&_svg]:tw-size-4 [&_svg]:tw-text-blue-500";

// A theme the user can pick for generation — either one of the account's saved
// design systems (id = `_id`) or a built-in Storydoc preset (id = `id`). Both
// flow into `selectedDesignSystemId` and ride along as `options.designSystemId`.
// Used only to resolve the selected-theme pill's title + colors for display;
// the picker list itself is the editor's ThemesSelectPopover.
type ThemeOption = {
  id: string;
  title: string;
  colors: string[];
};

const toColors = (get: (key: keyof TDesignSystemRoot) => unknown) =>
  COLOR_KEYS.map((key) => get(key)).filter(
    (c): c is string => typeof c === "string" && !!c,
  );

export const ThemeSelector = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [themeOpen, setThemeOpen] = useState(false);
  // "Import from file" mode — use the uploaded file's own (PDF-derived) design
  // system. selectedDesignSystemId stays null so generation sends no
  // designSystemId and the backend's PDF palette is applied.
  // Default: the file's own design (Yaacov 2026-09-17); a theme chosen before Back is kept.
  const [useFileDesignSystem, setUseFileDesignSystem] = useState(
    () => useFileToStorydocStore.getState().selectedDesignSystemId == null,
  );
  const defaultDsId = useAccountStoreShallow((s) => s.account?.dsId);
  const { data: themes } = useGetThemesQuery({ enabled: true });
  const {
    selectedDesignSystemId,
    setSelectedDesignSystemId,
    setKeepDeckTheme,
    target,
  } = useFileToStorydocStoreShallow((s) => ({
    selectedDesignSystemId: s.selectedDesignSystemId,
    setSelectedDesignSystemId: s.setSelectedDesignSystemId,
    setKeepDeckTheme: s.setKeepDeckTheme,
    target: s.target,
  }));
  const isDeck = target === "deck";
  // Importing into an open deck starts on the deck's own theme: the slides
  // land in a story that already has a design system, and inheriting it is
  // what makes them look like they belong. selectedDesignSystemId stays null
  // so nothing is re-themed. Only meaningful for the "deck" target — the
  // dashboard import has no deck to inherit from.
  const [useDeckDesignSystem, setUseDeckDesignSystem] = useState(isDeck);
  const deckDesign = useAppSelector(getDesignSystem) as
    | TDesignSystemRoot
    | undefined;
  const deckColors = useMemo(
    () => toColors((k) => deckDesign?.[k]),
    [deckDesign],
  );

  const knownThemeIdsRef = useRef<CreatedThemeSnapshot>(null);

  const { isLocked, onLockedAction } = useLockedAction({
    feature: "design-system-save-to-library",
  });

  const { open: openDesignSystemDialog, isOpening } =
    useOpenDesignSystemDialog();

  // Combined lookup for the selected-theme pill (account themes + presets).
  const allOptions = useMemo<ThemeOption[]>(() => {
    const yours = (themes ?? []).map((t: TDesignSystem) => ({
      id: t._id,
      title: t.title,
      colors: toColors((k) => (t.designSystem as TDesignSystemRoot)?.[k]),
    }));
    const presets = storydocThemes.map((t: StorydocTheme) => ({
      id: t.id,
      title: t.title,
      colors: toColors((k) => t[k as keyof StorydocTheme]),
    }));
    return [...yours, ...presets];
  }, [themes]);

  // Default to the account theme once — unless the user chose the file's own
  // design system.
  useEffect(() => {
    if (
      !useFileDesignSystem &&
      !useDeckDesignSystem &&
      !selectedDesignSystemId &&
      defaultDsId
    ) {
      setSelectedDesignSystemId(defaultDsId);
    }
  }, [
    useFileDesignSystem,
    useDeckDesignSystem,
    defaultDsId,
    selectedDesignSystemId,
    setSelectedDesignSystemId,
  ]);

  // Mirror the displayed choice into the store, including the INITIAL one: a
  // deck import starts on "use deck theme" (useState(isDeck)) and the user may
  // never open the picker, so without this the runner sees the default as
  // "nothing chosen" and repaints the deck in the file's colours — which is
  // exactly what happened.
  useEffect(() => {
    setKeepDeckTheme(useDeckDesignSystem);
  }, [useDeckDesignSystem, setKeepDeckTheme]);

  const selected =
    allOptions.find((o) => o.id === selectedDesignSystemId) ?? allOptions[0];

  useEffect(() => {
    const { snapshot, createdId } = resolveCreatedTheme(
      knownThemeIdsRef.current,
      themes,
    );
    knownThemeIdsRef.current = snapshot;
    if (!createdId) return;
    setUseFileDesignSystem(false);
    setUseDeckDesignSystem(false);
    setKeepDeckTheme(false);
    setSelectedDesignSystemId(createdId);
    trackImportPreJob("theme changed", {
      themeId: createdId,
      fromFile: false,
      created: true,
    });
  }, [themes, setSelectedDesignSystemId, setKeepDeckTheme]);

  const handleSelectTheme = (themeId: string) => {
    knownThemeIdsRef.current = null;
    setUseFileDesignSystem(false);
    setUseDeckDesignSystem(false);
    setKeepDeckTheme(false);
    setSelectedDesignSystemId(themeId);
    setThemeOpen(false);
    setMenuOpen(false);
    trackImportPreJob("theme changed", { themeId, fromFile: false });
  };

  const handleImportFromFile = () => {
    knownThemeIdsRef.current = null;
    setUseFileDesignSystem(true);
    setUseDeckDesignSystem(false);
    setKeepDeckTheme(false);
    setSelectedDesignSystemId(null);
    setThemeOpen(false);
    setMenuOpen(false);
    trackImportPreJob("theme changed", { themeId: null, fromFile: true });
  };

  const handleUseDeckTheme = () => {
    knownThemeIdsRef.current = null;
    setUseFileDesignSystem(false);
    setUseDeckDesignSystem(true);
    setKeepDeckTheme(true);
    setSelectedDesignSystemId(null);
    setThemeOpen(false);
    setMenuOpen(false);
  };

  const handleCreateNewTheme = () => {
    track("click create theme", { "is locked": isLocked });
    setThemeOpen(false);
    setMenuOpen(false);
    if (isLocked) {
      onLockedAction();
      return;
    }
    if (isOpening) return;
    knownThemeIdsRef.current = snapshotThemeIds(themes);
    const defaultTheme = themes?.find((theme) => theme._id === defaultDsId);
    const seedDesignSystem =
      (defaultTheme?.designSystem as Record<string, unknown> | undefined) ??
      (DEFAULT_DESIGN_SYSTEM as unknown as Record<string, unknown>);
    void openDesignSystemDialog({
      _id: "",
      designSystem: seedDesignSystem,
      title: "",
    });
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      <div className="tw-flex tw-items-center tw-justify-between">
        <span className="tw-text-base tw-font-semibold tw-text-blue">
          Design theme
        </span>
        <div className="tw-flex tw-items-center tw-gap-2">
          {/* The "Change" menu (popover #1). Stays open while the theme picker
            (popover #2) or a theme's action menu is open, via excludeSelectors. */}
          <Popover
            placement="bottom-end"
            open={menuOpen}
            onOpenChange={setMenuOpen}
            excludeSelectors={[
              ".theme-select-flyout",
              ".theme-item-actions-popover",
            ]}
          >
            <Popover.Trigger asChild>
              <button
                type="button"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                className="tw-flex tw-h-9 tw-cursor-pointer tw-items-center tw-gap-4 tw-rounded-xl tw-bg-white tw-px-3 tw-py-2.5 tw-shadow-2xs tw-outline tw-outline-1 tw-outline-offset-0 tw-outline-blue/5 hover:tw-bg-blue-50"
              >
                {useDeckDesignSystem ? (
                  // Deck theme selected — the imported slides inherit the
                  // design system of the story they land in.
                  <>
                    <span className="tw-mr-2 tw-truncate tw-text-sm/none tw-font-medium tw-text-blue">
                      Deck theme
                    </span>
                    <div className="tw-flex tw-items-center">
                      {deckColors.slice(0, 4).map((color, i) => (
                        <span
                          key={`${color}-${i}`}
                          className="-tw-mr-2 tw-size-6 tw-rounded-full tw-bg-white tw-shadow-xs tw-outline tw-outline-1 tw-outline-blue/5 last:tw-mr-0"
                          style={{ backgroundColor: color || "#FFFFFF" }}
                        />
                      ))}
                    </div>
                  </>
                ) : useFileDesignSystem ? (
                  // File design system selected — the palette comes from the
                  // uploaded file, so show the source instead of color swatches.
                  <span className="tw-flex tw-items-center tw-gap-2">
                    <IconFileSpark className="tw-size-4 tw-shrink-0 tw-text-blue" />
                    <span className="tw-truncate tw-text-sm tw-font-medium tw-text-blue">
                      From the file
                    </span>
                  </span>
                ) : (
                  selected && (
                    <>
                      <span className="tw-mr-2 tw-truncate tw-text-sm/none tw-font-medium tw-text-blue">
                        {selected.title}
                      </span>
                      <div className="tw-flex tw-items-center">
                        {selected.colors.slice(0, 4).map((color, i) => (
                          <span
                            key={`${color}-${i}`}
                            className="-tw-mr-2 tw-size-6 tw-rounded-full tw-bg-white tw-shadow-xs tw-outline tw-outline-1 tw-outline-blue/5 last:tw-mr-0"
                            style={{ backgroundColor: color || "#FFFFFF" }}
                          />
                        ))}
                      </div>
                    </>
                  )
                )}
                <IconSelector className="tw-size-4 tw-shrink-0 tw-text-blue" />
              </button>
            </Popover.Trigger>
            <Popover.Content className="tw-z-[99999]">
              <div className="tw-w-[210px] tw-rounded-lg tw-bg-white tw-p-1 tw-shadow-sm tw-outline tw-outline-1 tw-outline-blue/5">
                {/* Select theme — opens the editor's ThemesSelectPopover to the
                  right (popover #2). */}
                <Popover
                  placement="right-start"
                  open={themeOpen}
                  onOpenChange={setThemeOpen}
                  excludeSelectors={[".theme-item-actions-popover"]}
                >
                  <Popover.Trigger asChild>
                    <button
                      type="button"
                      className={cn(ROW_CLASS, themeOpen && "tw-bg-[#F3F2F5]")}
                    >
                      <IconPalette />
                      <span>Select theme</span>
                      <IconChevronRight className="tw-ml-auto" />
                    </button>
                  </Popover.Trigger>
                  <Popover.Content className="theme-select-flyout tw-z-[99999]">
                    <div className="tw-w-[340px] tw-rounded-lg tw-bg-white tw-shadow-sm tw-outline tw-outline-1 tw-outline-blue/5">
                      <ThemesSelectPopover
                        isOpen={themeOpen}
                        close={() => {
                          setThemeOpen(false);
                          setMenuOpen(false);
                        }}
                        onSelectTheme={handleSelectTheme}
                      />
                    </div>
                  </Popover.Content>
                </Popover>

                {/* Back to the open deck's own design system (deck imports). */}
                {isDeck && (
                  <button
                    type="button"
                    className={ROW_CLASS}
                    onClick={handleUseDeckTheme}
                  >
                    <IconLayoutBoard />
                    <span>Use deck theme</span>
                  </button>
                )}

                {/* Use the uploaded file's own (PDF-derived) design system. */}
                <button
                  type="button"
                  className={ROW_CLASS}
                  onClick={handleImportFromFile}
                >
                  <IconFileSpark />
                  <span>Import from file</span>
                </button>

                <button
                  type="button"
                  className={ROW_CLASS}
                  onClick={handleCreateNewTheme}
                >
                  <IconPlus />
                  <span>Create new</span>
                  <UpgradeLabel
                    feature="design-system-save-to-library"
                    className="tw-ml-auto"
                  />
                </button>
              </div>
            </Popover.Content>
          </Popover>
        </div>
      </div>
      {/* A design system belongs to the story, not to individual slides — so
          picking anything other than the deck's own theme repaints every
          slide, not just the imported ones. Say so before they press
          Continue. */}
      {isDeck && !useDeckDesignSystem && (
        <p className="tw-text-xs tw-text-blue-500">
          Applies to the whole deck, not only the imported slides.
        </p>
      )}
    </div>
  );
};
